import React, { useEffect, useState } from 'react';
import { Tags, Plus, Pencil, Trash2, GitMerge, Loader2, Check, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function TagsTab() {
  const { getAdminTags, addTag, renameTag, mergeTags, deleteTag, setTagColor } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState('');
  const [editing, setEditing] = useState(null); // {name, value}
  const [selected, setSelected] = useState([]);
  const [mergeTarget, setMergeTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); getAdminTags().then((t) => setTags(Array.isArray(t) ? t : [])).catch(() => setTags([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doAdd = async () => {
    if (!newTag.trim()) return;
    setBusy(true);
    try { await addTag(newTag.trim()); setNewTag(''); load(); } finally { setBusy(false); }
  };
  const doRename = async () => {
    if (!editing || !editing.value.trim()) return;
    setBusy(true);
    try { await renameTag(editing.name, editing.value.trim()); setEditing(null); load(); } finally { setBusy(false); }
  };
  const doDelete = async (name) => {
    if (!window.confirm(`Delete tag "#${name}"? It will be removed from all photos and members.`)) return;
    setBusy(true);
    try { await deleteTag(name); load(); } finally { setBusy(false); }
  };
  const toggleSel = (name) => setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));
  const doMerge = async () => {
    if (selected.length < 1 || !mergeTarget.trim()) return;
    setBusy(true);
    try { await mergeTags(selected, mergeTarget.trim()); setSelected([]); setMergeTarget(''); load(); } finally { setBusy(false); }
  };
  const changeColor = async (name, color) => {
    setTags((ts) => ts.map((t) => (t.name === name ? { ...t, color } : t)));
    try { await setTagColor(name, color); } catch (e) { /* revert on next load */ load(); }
  };

  return (
    <div className="max-w-3xl" data-testid="tags-panel">
      <div className="flex items-center gap-2 mb-1">
        <Tags size={18} style={{ color: 'var(--maroon)' }} />
        <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Tags</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>
        One shared list of tags used across the photo gallery and the member directory. Rename or merge to tidy up —
        changes apply everywhere the tag is used.
      </p>

      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Add a tag</label>
        <div className="flex gap-2">
          <input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doAdd()}
            placeholder="e.g. reunion" data-testid="tag-new-input"
            className="flex-1 px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
          <button onClick={doAdd} disabled={busy || !newTag.trim()} data-testid="tag-add-btn"
            className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
            <Plus size={15} /> Add
          </button>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="tag-merge-box">
          <div className="flex items-center gap-2 mb-2 text-sm font-title" style={{ color: 'var(--navy)' }}>
            <GitMerge size={15} /> Merge {selected.length} selected tag{selected.length === 1 ? '' : 's'} into:
          </div>
          <div className="flex gap-2">
            <input value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} placeholder="target tag name" data-testid="tag-merge-target"
              className="flex-1 px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
            <button onClick={doMerge} disabled={busy || !mergeTarget.trim()} data-testid="tag-merge-btn"
              className="px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>Merge</button>
            <button onClick={() => setSelected([])} className="px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Clear</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <table className="w-full text-sm" data-testid="tags-table">
          <thead><tr style={{ background: 'var(--navy)' }}>
            <th className="px-3 py-2.5 w-8"></th>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Tag</th>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Colour</th>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Photos</th>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Members</th>
            <th className="px-3 py-2.5 text-right font-title" style={{ color: 'var(--cream)' }}>Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-4 text-center"><Loader2 className="animate-spin inline" size={16} /></td></tr>
            ) : tags.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No tags yet.</td></tr>
            ) : tags.map((t) => (
              <tr key={t.name} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`tag-row-${t.name}`}>
                <td className="px-3 py-2"><input type="checkbox" checked={selected.includes(t.name)} onChange={() => toggleSel(t.name)} data-testid={`tag-select-${t.name}`} className="accent-[var(--maroon)]" /></td>
                <td className="px-3 py-2">
                  {editing && editing.name === t.name ? (
                    <span className="inline-flex items-center gap-1">
                      <input value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && doRename()}
                        data-testid="tag-rename-input" autoFocus className="px-2 py-1 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                      <button onClick={doRename} data-testid="tag-rename-save" style={{ color: 'var(--maroon)' }}><Check size={16} /></button>
                      <button onClick={() => setEditing(null)} style={{ color: 'var(--olive)' }}><X size={16} /></button>
                    </span>
                  ) : (
                    <span className="font-title" style={{ color: 'var(--maroon)' }}>#{t.name}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5">
                    <input type="color" value={t.color || '#8A0A06'} onChange={(e) => changeColor(t.name, e.target.value)}
                      data-testid={`tag-color-${t.name}`} title="Set tag colour"
                      className="w-8 h-6 rounded cursor-pointer border-0 bg-transparent p-0" />
                    {t.color ? (
                      <button onClick={() => changeColor(t.name, '')} data-testid={`tag-color-clear-${t.name}`} title="Clear colour"
                        className="text-xs underline" style={{ color: 'var(--olive)' }}>clear</button>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--olive)' }}>default</span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2">{t.photos}</td>
                <td className="px-3 py-2">{t.members}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing({ name: t.name, value: t.name })} title="Rename" data-testid={`tag-edit-${t.name}`} className="mr-2"><Pencil size={15} style={{ color: 'var(--navy)' }} /></button>
                  <button onClick={() => doDelete(t.name)} title="Delete" data-testid={`tag-delete-${t.name}`}><Trash2 size={15} style={{ color: '#8a1f11' }} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
